/**
 * @file Collision helpers shared by the collision system and the AI.
 *
 * Pure functions over components — no world access, no side effects — so they
 * can be reused by any future system (pathfinding, line of sight, explosions).
 */

/**
 * @typedef {object} Bounds
 * @property {number} minX Minimum X.
 * @property {number} minZ Minimum Z.
 * @property {number} maxX Maximum X.
 * @property {number} maxZ Maximum Z.
 */

/**
 * @returns {Bounds} A reusable bounds object.
 */
export function createBounds() {
  return { minX: 0, minZ: 0, maxX: 0, maxZ: 0 };
}

/**
 * Computes the world-space bounds of an entity's collider.
 *
 * @param {import('../components/Transform.js').Transform} transform Entity transform.
 * @param {import('../components/Collider.js').Collider} collider Entity collider.
 * @param {Bounds} out Result, mutated in place.
 * @returns {Bounds} The same `out` object.
 */
export function boundsOf(transform, collider, out) {
  out.minX = transform.position.x - collider.halfX;
  out.maxX = transform.position.x + collider.halfX;
  out.minZ = transform.position.z - collider.halfZ;
  out.maxZ = transform.position.z + collider.halfZ;
  return out;
}

/**
 * Expands bounds by a margin, used to widen broad-phase queries for movement.
 * @param {Bounds} bounds Bounds to grow, mutated in place.
 * @param {number} margin Amount added on every side.
 * @returns {Bounds} The same bounds.
 */
export function expandBounds(bounds, margin) {
  bounds.minX -= margin;
  bounds.minZ -= margin;
  bounds.maxX += margin;
  bounds.maxZ += margin;
  return bounds;
}

/**
 * Grows bounds to contain a swept motion, so a fast body is broad-phased over
 * the whole segment it travels this frame.
 * @param {Bounds} bounds Bounds to grow, mutated in place.
 * @param {number} dx Motion along X.
 * @param {number} dz Motion along Z.
 * @returns {Bounds} The same bounds.
 */
export function sweepBounds(bounds, dx, dz) {
  if (dx > 0) bounds.maxX += dx;
  else bounds.minX += dx;
  if (dz > 0) bounds.maxZ += dz;
  else bounds.minZ += dz;
  return bounds;
}

/**
 * Layer/mask filter: two colliders interact only when each one's mask includes
 * the other's layer. Adding a new interaction is a config change, never a
 * change to the collision system (GDD §23).
 *
 * @param {import('../components/Collider.js').Collider} a First collider.
 * @param {import('../components/Collider.js').Collider} b Second collider.
 * @returns {boolean} True when the pair should be tested.
 */
export function canCollide(a, b) {
  return (a.mask & b.layer) !== 0 && (b.mask & a.layer) !== 0;
}
